import * as React from "react";
import type { ReactNode } from "react";
import { ClerkProvider } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { useClerkAppearance } from "@/shared/theme/lib/use-clerk-appearance";
import { NextIntlClientProvider } from "@/i18n/intl-react";

import { AuthAccessSync } from "@/features/auth/components/auth-access-sync";
import { AuthorizationRevisionSync } from "@/features/auth/components/authorization-revision-sync";
import { AppAbilityProvider } from "@/features/auth/casl/app-ability-context";
import { useAuthContextStore } from "@/features/auth/services/auth-context-store";
import { TenantAuthFlowProvider, TenantAuthLoadingGate } from "@/features/auth/providers/tenant-auth-flow-provider";
import { PostHogIdentitySync } from "@/app/posthog-identity-sync";
import { PostHogFlagRefresh } from "@/app/posthog-flag-refresh";
import { HttpAuthSync } from "@/shared/components/http-auth-sync";
import { MobileInputFocusGuard } from "@/shared/components/mobile-input-focus-guard";
import { PwaUpdatePrompt } from "@/shared/components/pwa-update-prompt";
import { AssistantLauncher } from "@/features/assistant";
import { TenantDashboardEngineLabels } from "@/shared/dashboard-engine/dashboard-engine-labels-provider"
import { QueryProvider } from "@/shared/components/query-provider";
import { LocaleQuerySync } from "@/shared/components/locale-query-sync";
import { ThemeProvider } from "@/shared/theme/components/theme-provider";
import { Toaster } from "@ibada/ui/components/sonner";
import { TooltipProvider } from "@ibada/ui/components/tooltip";
import { UiDateLocaleProvider } from "@ibada/ui";
import { TenantDataTableMobileLabels, TenantMobilePickerLabels } from "@/shared/components/data-table-mobile-labels";
import { getDateFnsLocale } from "@/shared/lib/dates/date-fns-locale";
import { loadLocaleResources, type LocaleResources } from "@/i18n/locale-resources";
import { getUrlLocale, persistLocaleOverride, resolveRuntimeLocale } from "@/i18n/locale-resolution";
import { LocaleRuntimeProvider } from "@/i18n/locale-runtime";
import type { AppLocale } from "@/i18n/routing";
import { LayoutDebugPanel } from "@/shared/components/layout-debug-panel";

function AppBootstrapFallback() {
  return (
    <div className='flex min-h-[var(--app-height)] w-full items-center justify-center bg-background'>
      <div className='size-10 animate-spin rounded-full border-2 border-border border-t-primary' />
    </div>
  );
}

export function AppProviders({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const authMe = useAuthContextStore((state) => state.authMe);
  const activeWorkspaceSlug = useAuthContextStore((state) => state.activeWorkspaceSlug);
  const authMeAttempted = useAuthContextStore((state) => state.authMeAttempted);
  const activeWorkspaceDefaultLanguage = authMe?.workspaceAccess.find(
    (entry) => entry.workspace.slug === activeWorkspaceSlug,
  )?.workspace.defaultLanguage;
  const resolvedLocale = resolveRuntimeLocale({
    authMeLoaded: Boolean(authMe) || authMeAttempted,
    activeWorkspaceDefaultLanguage,
    activeWorkspaceSlug,
  });
  const [runtimeLocale, setRuntimeLocale] = React.useState<AppLocale>(resolvedLocale);
  const [localeResources, setLocaleResources] = React.useState<LocaleResources | null>(null);
  const [localeResourcesError, setLocaleResourcesError] = React.useState<Error | null>(null);
  const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const clerkAppearance = useClerkAppearance();

  React.useEffect(() => {
    setRuntimeLocale((current) => (current === resolvedLocale ? current : resolvedLocale));
  }, [resolvedLocale]);

  // Persist an explicit `?lang=` (marketing-site deep link into login/create-church) to the locale
  // cookie so the chosen language survives navigation and the Clerk round-trip, not just the first
  // render. Pre-auth only — once signed in, the workspace default / saved override take over.
  React.useEffect(() => {
    const urlLocale = getUrlLocale();
    if (urlLocale) {
      persistLocaleOverride({ locale: urlLocale, rememberAsOverride: false });
      setRuntimeLocale(urlLocale);
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    setLocaleResourcesError(null);

    void loadLocaleResources(runtimeLocale)
      .then((resources) => {
        if (cancelled) {
          return;
        }

        setLocaleResources(resources);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        setLocaleResourcesError(error instanceof Error ? error : new Error(String(error)));
      });

    return () => {
      cancelled = true;
    };
  }, [runtimeLocale]);

  if (localeResourcesError) {
    throw localeResourcesError;
  }

  if (!localeResources) {
    return <AppBootstrapFallback />;
  }

  const isLocaleChanging = localeResources.locale !== runtimeLocale;

  return (
    <LocaleRuntimeProvider isLocaleChanging={isLocaleChanging} setLocale={setRuntimeLocale}>
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        routerPush={(to) => navigate(to)}
        routerReplace={(to) => navigate(to, { replace: true })}
        signInUrl='/login'
        // Clerk account creation must remain the neutral account route. Product-led church creation lives at
        // /create-church, but invitations need Clerk's "create account" fallback to land on /register.
        signUpUrl='/register'
        signInForceRedirectUrl='/dashboard'
        signUpForceRedirectUrl='/dashboard'
        signInFallbackRedirectUrl='/dashboard'
        signUpFallbackRedirectUrl='/dashboard'
        appearance={clerkAppearance}
        localization={localeResources.clerkLocalization}
      >
        <NextIntlClientProvider locale={localeResources.locale} messages={localeResources.messages}>
          <UiDateLocaleProvider locale={getDateFnsLocale(localeResources.locale)}>
          <MobileInputFocusGuard />
          <HttpAuthSync />
          <QueryProvider>
            <LocaleQuerySync />
            <TenantDashboardEngineLabels>
            <TenantDataTableMobileLabels>
            <TenantMobilePickerLabels>
            <AuthAccessSync />
            <TenantAuthFlowProvider>
              <AuthorizationRevisionSync />
              <PostHogIdentitySync />
              <PostHogFlagRefresh />
              <TenantAuthLoadingGate>
                <AppAbilityProvider>
                  <TooltipProvider>
                    <ThemeProvider>
                      <>
                        <div className='flex min-h-0 w-full flex-1 flex-col'>{children}</div>
                        <PwaUpdatePrompt />
                        <AssistantLauncher />
                        <Toaster />
                        <LayoutDebugPanel />
                      </>
                    </ThemeProvider>
                  </TooltipProvider>
                </AppAbilityProvider>
              </TenantAuthLoadingGate>
            </TenantAuthFlowProvider>
            </TenantMobilePickerLabels>
            </TenantDataTableMobileLabels>
            </TenantDashboardEngineLabels>
          </QueryProvider>
          </UiDateLocaleProvider>
        </NextIntlClientProvider>
      </ClerkProvider>
    </LocaleRuntimeProvider>
  );
}
